import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import { useIsBrandLive, useLiveRefetchInterval } from "@/lib/socket";
import type { FactProposal } from "@/types/chat.type";

export const brandUniversePendingFactProposalsKey = (brandId: string | null) =>
  ["brandUniversePendingFactProposals", brandId] as const;

/**
 * Brand-global pending fact proposals (any chat session). Powers the universe
 * facts-rail review overlay — proposals are not scoped to the open chat.
 */
export function useBrandUniversePendingFactProposals(brandId: string | null) {
  const queryClient = useQueryClient();
  const live = useIsBrandLive(brandId);
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_CURATION_MS);

  const query = useQuery({
    queryKey: brandUniversePendingFactProposalsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ proposals: FactProposal[] }>(
        `/brand/${brandId}/universe/fact-proposals/pending`,
      );
      return res.data.proposals;
    },
    enabled: !!brandId,
    // Proposals arrive from other users' chat sessions, pushed as realtime
    // events while the brand is live; polled otherwise.
    refetchInterval,
    refetchOnWindowFocus: !live,
  });

  const pending = useMemo(() => query.data ?? [], [query.data]);

  const invalidatePending = useCallback(() => {
    if (!brandId) return;
    void queryClient.invalidateQueries({
      queryKey: brandUniversePendingFactProposalsKey(brandId),
    });
  }, [queryClient, brandId]);

  return {
    pending,
    isLoading: query.isLoading,
    // Exposed so a consumer can tell "no proposals" apart from "the queue could
    // not be read". An empty list on a failed query is a fake zero.
    isError: query.isError,
    invalidatePending,
  };
}
